import { useCallback } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Clickable from 'components/Common/Clickable'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import LineArrowsSwapHorizontalIcon from 'LuxKit/Icons/line/LineArrowsSwapHorizontalIcon'
import { getCheckInCheckOutText } from 'components/Search/utils'
import useOrderFlightDetails from 'hooks/Flights/useOrderFlightDetails'
import { INTL_DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { deriveFlightDisplayData, formatFlightFallbackTitle, formatFlightTypeLabel } from './flightDisplayUtils'

type DisplayVariant = 'default' | 'addedToTrip'
const ADDED_TO_TRIP_CLASS_NAME = 'added-to-trip'

const Container = styled(Clickable)`
  width: ${rem(240)};
  min-height: ${rem(110)};
  flex-shrink: 0;

  &.${ADDED_TO_TRIP_CLASS_NAME} {
    width: ${rem(300)};
    min-height: ${rem(68)};
  }

  > * {
    height: 100%;
  }
`

const CompactContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`

interface Props {
  itemId: string
  orderId: string
  selected?: boolean
  disabled?: boolean
  displayVariant?: DisplayVariant
  onClick: (itemId: string) => void
}

const AirlineLogo = styled.img`
  width: ${rem(28)};
  height: ${rem(28)};
  object-fit: contain;
  flex-shrink: 0;
`

export default function ChatLandingFlightOrderTile({
  itemId,
  orderId,
  selected,
  disabled = false,
  displayVariant = 'default',
  onClick,
}: Props) {
  const intl = useIntl()
  const isAddedToTripVariant = displayVariant === 'addedToTrip'
  const displayVariantClassName = isAddedToTripVariant ? ADDED_TO_TRIP_CLASS_NAME : undefined
  const [{ flightDetails }] = useOrderFlightDetails(orderId, [itemId])

  const {
    departureDate,
    arrivalDate,
    departureLabel,
    arrivalLabel,
    airlineLogoUrl,
    isReturnFlight,
    isDirectFlight,
  } = deriveFlightDisplayData(flightDetails)

  const subtitleParts: Array<string> = []
  if (departureDate && arrivalDate && isReturnFlight) {
    subtitleParts.push(getCheckInCheckOutText(departureDate, arrivalDate))
  } else if (departureDate) {
    subtitleParts.push(intlDateFormatter(departureDate, INTL_DMY_CASUAL_SHORT_FORMAT, intl.locale))
  }
  subtitleParts.push(formatFlightTypeLabel(intl, isReturnFlight, isDirectFlight))
  const subtitle = subtitleParts.join(' · ')

  const routeIcon = isReturnFlight ? <LineArrowsSwapHorizontalIcon size="S" /> : <LineArrowRightIcon size="S" />
  const hasRoute = Boolean(departureLabel && arrivalLabel)
  const fallbackTitle = formatFlightFallbackTitle(intl)
  const yourFlightLabel = intl.formatMessage({
    id: 'abIxvLb_sFmDPtI99kll',
    defaultMessage: 'Your flight',
    description: 'Category label for flight booking tile on the chat landing page',
  })

  const handleClick = useCallback(() => {
    if (disabled) {
      return
    }

    onClick(itemId)
  }, [disabled, onClick, itemId])

  const tileContent = (
    <Group
      direction="horizontal"
      gap={8}
      verticalAlign="center"
      fullWidth
    >
      {airlineLogoUrl && <AirlineLogo src={airlineLogoUrl} alt="" aria-hidden="true" />}
      <Group direction="vertical" gap={2} grow>
        {hasRoute && (
          <BodyText
            variant="medium"
            weight="normal"
            lineClamp={isAddedToTripVariant ? 1 : 2}
          >
            {departureLabel} {routeIcon} {arrivalLabel}
          </BodyText>
        )}
        {!hasRoute && (
          <BodyText
            variant="medium"
            weight="normal"
            lineClamp={isAddedToTripVariant ? 1 : 2}
          >
            {fallbackTitle}
          </BodyText>
        )}
        {subtitle && (
          <Caption variant="medium" colour="light-foreground-alt" lineClamp={1}>
            {subtitle}
          </Caption>
        )}
      </Group>
    </Group>
  )

  const compactContent = (
    <CompactContent>{tileContent}</CompactContent>
  )

  const containerContent = isAddedToTripVariant ? compactContent : (
    <Group direction="vertical" gap={12} verticalAlign="center" fullHeight>
      <CaptionTextBlock
        variant="small"
        weight="bold"
        format="uppercase"
        colour="light-foreground-alt"
        startIcon={<LinePlaneDepartureIcon size="XS" />}
      >
        {yourFlightLabel}
      </CaptionTextBlock>
      {tileContent}
    </Group>
  )

  return (
    <Container
      onClick={handleClick}
      disabled={disabled}
      className={displayVariantClassName}
    >
      <Sheet
        background="normal-background"
        border={selected ? 'normal-contrast' : 'light-foreground'}
        borderRadius="L"
        padding={isAddedToTripVariant ? 6 : 12}
      >
        {containerContent}
      </Sheet>
    </Container>
  )
}
